import { pagePath } from "./routes";

export interface Cluster {
  slug: string;
  path: string;
  name: string;
  cities: string[];
  blurb: string;
  metaDescription?: string;
}

const clusterData: Cluster[] = [
  {
    slug: "leicester-area",
    path: "/areas/leicester-area",
    name: "Leicester Area",
    cities: ["Leicester", "Spencer", "Paxton", "Rutland", "Oakham", "Charlton"],
    blurb:
      "The Leicester area runs from the hilltops of Paxton, Rutland, and Oakham down through Spencer and Charlton, a stretch of older farmhouses, colonials, and newer builds set back on wooded lots. Homeowners here tend to want a clean living-room television setup, whole-house and outdoor audio for the deck and yard, and surround sound for a finished basement, all wired so it survives a New England winter. Along the Main Street stretches in Leicester and Spencer we also outfit restaurants, bars, and small offices with sound and TV systems that hold up to a full house.",
  },
  {
    slug: "worcester-area",
    path: "/areas/worcester-area",
    name: "Worcester Area",
    cities: ["Worcester", "Auburn", "Holden", "Shrewsbury", "West Boylston"],
    blurb:
      "The Worcester area is the busiest part of our territory, from the triple-deckers and revitalized downtown of the city itself out to the family neighborhoods of Holden, Shrewsbury, Auburn, and West Boylston. City homes often need tidy installs that respect plaster walls and tight rooms, while the suburbs lean toward larger living-room and basement theater builds and multi-zone outdoor audio. We also keep Worcester's restaurants, bars, gyms, and offices running with sound and display systems built for long hours and steady use.",
  },
  {
    slug: "brookfield-area",
    path: "/areas/brookfield-area",
    name: "Brookfield Area",
    cities: ["East Brookfield", "Brookfield"],
    blurb:
      "East Brookfield and Brookfield are small, close-knit towns of antique homes, lake properties along the Quaboag, and country lots where the nearest neighbor is a field away. The work here leans residential: surround sound and large-display setups for living rooms and finished basements, plus outdoor speakers for patios, pools, and porches that want music without bothering the neighbors. We also handle the local taverns and gathering spots that need reliable TV and audio for game nights and weekend crowds.",
  },
  {
    slug: "sturbridge-area",
    path: "/areas/sturbridge-area",
    name: "Sturbridge Area",
    cities: ["Sturbridge", "Southbridge", "Brimfield"],
    blurb:
      "Sturbridge, Southbridge, and Brimfield sit where tourism meets small-city Main Street life, from the restaurants and shops near Old Sturbridge Village to the antique-show crowds that pour into Brimfield each year. The restaurants and bars here need TV and zoned audio that carries across dining rooms, bars, and patios, while the area's historic homes and newer subdivisions want surround sound and clean television installs that respect older construction. We cover both the storefronts and the living rooms with the same crew.",
  },
  {
    slug: "webster-area",
    path: "/areas/webster-area",
    name: "Webster Area",
    cities: ["Webster", "Oxford", "Dudley", "Thompson (CT)"],
    blurb:
      "The Webster area wraps around Webster Lake and reaches south into Oxford, Dudley, and just over the line into Thompson, Connecticut. Lakefront homes here want outdoor and dock audio plus living-room systems for the off-season, while the mill-town centers of Webster and Dudley keep us busy with restaurant, bar, and small-business TV and sound work. Whether it is a year-round house or a summer place, we wire it so it holds up season after season.",
  },
  {
    slug: "westborough-area",
    path: "/areas/westborough-area",
    name: "Westborough Area",
    cities: ["Northborough", "Westborough", "Upton"],
    blurb:
      "Northborough, Westborough, and Upton sit along the Route 9 and I-495 corridor, a mix of newer subdivisions, established colonials, and the office parks that line the commercial stretches. Homes here often want dedicated theater rooms, whole-house audio, and smart-home integration done cleanly during a build or remodel. On the commercial side we outfit the area's offices and conference rooms with displays, video conferencing, and audio that staff can actually run without calling for help.",
  },
  {
    slug: "marlborough-area",
    path: "/areas/marlborough-area",
    name: "Marlborough Area",
    cities: ["Marlborough", "Southborough", "Hudson", "Hopkinton"],
    blurb:
      "The Marlborough area spans corporate Marlborough, the upscale homes of Southborough and Hopkinton, and the revitalized downtown of Hudson with its restaurants and breweries. Residential projects range from living-room television installs to finished-basement theaters and multi-zone outdoor audio for the larger lots. The business community keeps us busy too, with conference-room AV and video conferencing for the office parks and sound and TV systems for Hudson's growing restaurant scene.",
  },
  {
    slug: "milford-area",
    path: "/areas/milford-area",
    name: "Milford Area",
    cities: ["Milford", "Hopedale", "Mendon", "Uxbridge"],
    metaDescription:
      "Home theater, sound system, commercial AV, and Wi-Fi/surveillance installation in the Milford Area — Milford, Hopedale, Mendon, and Uxbridge, MA. Call 508-450-0490.",
    blurb:
      "Milford, Hopedale, Mendon, and Uxbridge range from the dense, historic streets of Milford and the planned mill village of Hopedale to the open country lots of Mendon and Uxbridge, where the Blackstone River valley's older farmhouses and newer subdivisions both call for careful whole-home audio and outdoor speaker runs. Homeowners here ask for clean television mounting, surround sound, and outdoor speakers for decks and pools, often in older homes that need a careful hand with the wiring. Along Milford's busy commercial strips we handle restaurants, bars, and offices that need TV and audio built for daily use.",
  },
  {
    slug: "framingham-area",
    path: "/areas/framingham-area",
    name: "Framingham Area",
    cities: ["Framingham", "Natick", "Ashland", "Sherborn, MA"],
    blurb:
      "The Framingham area is the MetroWest hub, denser and busier than the rest of our territory, taking in Framingham, Natick, Ashland, and Sherborn with their retail centers, restaurants, commuter neighborhoods, and wooded residential streets. Homes range from in-town capes and colonials to newer construction, with owners wanting tidy television installs, surround sound, home theater, and whole-house audio. The commercial side is strong here too, from conference-room AV and video conferencing for area offices to sound and display systems for the restaurants and storefronts along Routes 9 and 135.",
  },
];

export const CLUSTERS: Cluster[] = clusterData.map((cluster) => ({
  ...cluster,
  path: pagePath(cluster.path),
}));

export interface ServiceMeta {
  slug: string;
  path: string;
  title: string;
  short: string;
}

const serviceData: ServiceMeta[] = [
  { slug: "home-theater-systems",  path: "/services/home-theater-systems",  title: "Home Theater Systems",  short: "Dedicated theater rooms, surround sound, and large-format display systems installed end-to-end." },
  { slug: "sound-systems",         path: "/services/sound-systems",         title: "Sound Systems",         short: "Distributed audio, commercial sound, and zone control for any space." },
  { slug: "commercial-av-solutions", path: "/services/commercial-av-solutions", title: "Commercial AV Solutions", short: "Easy-to-use commercial solutions for all your AV problems." },
  { slug: "residential-wifi-surveillance", path: "/services/residential-wifi-surveillance", title: "Residential Wi-Fi Surveillance", short: "Wireless security cameras, smart home security, and professional camera placement for Central MA homes." },
];

export const SERVICES: ServiceMeta[] = serviceData.map((service) => ({
  ...service,
  path: pagePath(service.path),
}));
